'use client';

import { useState, useCallback, useRef } from 'react';
import { useAuth } from '@/context/AuthContext/AuthContext';
import type { BirthdateRejection } from '@/utils';

/** Everything the modal can need to explain itself, plus the write-once refusal. */
export type AgeVerificationError = BirthdateRejection | 'SAVE_FAILED';

interface UseAgeVerificationResult {
  saving: boolean;
  error: AgeVerificationError | null;
  hasVerifiedAge: boolean;
  submit: (isAdult: boolean, birthdate?: string | null) => Promise<boolean>;
  clearError: () => void;
}

interface VerifyAgeErrorBody {
  error?: { message?: string; details?: { reason?: string } };
}

const KNOWN_REASONS: readonly AgeVerificationError[] = [
  'BIRTHDATE_REQUIRED',
  'BIRTHDATE_INVALID',
  'BIRTHDATE_IN_FUTURE',
  'AGE_OUT_OF_RANGE',
];

function toKnownReason(reason: unknown): AgeVerificationError | null {
  return KNOWN_REASONS.find((known) => known === reason) ?? null;
}

/**
 * True when the signed-in member still owes us an answer to the age question.
 *
 * The single definition of "pending", shared by the gate that shows the age
 * modal and by CommunityGuidelinesGate, which suppresses itself while the age
 * question is outstanding so the two blocking modals never stack (MWBI-899, and
 * the ordering the client signed off on: age first, then guidelines).
 */
export function useAgeVerificationPending(): boolean {
  const { isAuthenticated, currentUser } = useAuth();
  return isAuthenticated && !currentUser?.ageVerifiedAt;
}

/**
 * Wraps the POST /users/me/verify-age call (MWBI-899).
 *
 * Records the member's answer to "Are you 18 or older?" server-side, then
 * optimistically flips local auth state so the blocking gate closes immediately.
 * `AgeVerificationGate` drives the modal off `hasVerifiedAge`; this hook is the
 * single place an answer is recorded.
 *
 * Mirrors useCommunityGuidelines, with one addition it cannot share: the
 * endpoint is write-once, so a second answer is refused. That refusal has to be
 * treated as SUCCESS here, not as an error — otherwise a member who answers in
 * two tabs (or whose local latch was dropped) would be trapped behind a modal
 * that blocks the whole app and rejects every attempt to clear it.
 */
export function useAgeVerification(): UseAgeVerificationResult {
  const { currentUser, refreshCurrentUser, patchCurrentUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<AgeVerificationError | null>(null);
  // Synchronous in-flight guard: `disabled={saving}` only takes effect after a
  // re-render, so a fast double-click could otherwise fire two POSTs — and with
  // a write-once endpoint the second would come back refused.
  const inFlight = useRef(false);

  const submit = useCallback(
    async (isAdult: boolean, birthdate?: string | null): Promise<boolean> => {
      if (inFlight.current) return false;
      inFlight.current = true;
      setSaving(true);
      setError(null);

      try {
        const res = await fetch('/api/strapi/users/me/verify-age', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ isAdult, birthdate: isAdult ? null : birthdate ?? null }),
        });

        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as VerifyAgeErrorBody;
          const reason = body?.error?.details?.reason;

          // The answer is already on file. Nothing is wrong from the member's
          // point of view — re-sync and let the gate close.
          if (reason === 'ALREADY_ANSWERED') {
            await refreshCurrentUser();
            return true;
          }

          setError(toKnownReason(reason) ?? 'SAVE_FAILED');
          return false;
        }

        // Optimistically flip local state from the POST response so the gate
        // closes immediately — even if the background refresh below fails.
        try {
          const data = (await res.json()) as {
            ageVerifiedAt?: string;
            ageGroup?: 'age_18_plus' | 'under_18';
            birthdate?: string | null;
          };
          patchCurrentUser({
            ageVerifiedAt: data?.ageVerifiedAt ?? new Date().toISOString(),
            ...(data?.ageGroup ? { ageGroup: data.ageGroup } : {}),
            ...(data?.birthdate ? { birthdate: data.birthdate } : {}),
          });
        } catch (parseErr) {
          // The POST already succeeded; a body-parse failure is non-fatal. Fall
          // back to a client stamp so the gate still closes and the member is not
          // left staring at a modal whose answer was in fact saved.
          console.warn('verify-age response body parse failed; using client timestamp', parseErr);
          patchCurrentUser({ ageVerifiedAt: new Date().toISOString() });
        }

        // Fire-and-forget full hydration so ageGroup/birthdate stay fresh; the
        // member already counts as answered at this point.
        void refreshCurrentUser();
        return true;
      } catch (err) {
        console.error('Failed to save age answer:', err);
        setError('SAVE_FAILED');
        return false;
      } finally {
        setSaving(false);
        inFlight.current = false;
      }
    },
    [refreshCurrentUser, patchCurrentUser],
  );

  return {
    saving,
    error,
    hasVerifiedAge: Boolean(currentUser?.ageVerifiedAt),
    submit,
    clearError: useCallback(() => setError(null), []),
  };
}
